// Task and checklist templates, and recurring tasks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post, put } from "./client";
import type { ChecklistTemplate } from "./types/ChecklistTemplate";
import type { ChecklistTemplateRequest } from "./types/ChecklistTemplateRequest";
import type { FromTemplateRequest } from "./types/FromTemplateRequest";
import type { Recurrence } from "./types/Recurrence";
import type { RecurrenceRequest } from "./types/RecurrenceRequest";
import type { TaskDetail } from "./types/TaskDetail";
import type { TaskTemplate } from "./types/TaskTemplate";
import type { TaskTemplateRequest } from "./types/TaskTemplateRequest";

const enc = encodeURIComponent;
const base = (key: string) => `/projects/${enc(key)}`;

export function useTaskTemplates(projectKey: string, enabled = true) {
  return useQuery({ queryKey: ["templates", projectKey, "task"], queryFn: () => get<TaskTemplate[]>(`${base(projectKey)}/task-templates`), enabled });
}

export function useChecklistTemplates(projectKey: string, enabled = true) {
  return useQuery({ queryKey: ["templates", projectKey, "checklist"], queryFn: () => get<ChecklistTemplate[]>(`${base(projectKey)}/checklist-templates`), enabled });
}

export function useRecurrences(projectKey: string) {
  return useQuery({ queryKey: ["templates", projectKey, "recurrences"], queryFn: () => get<Recurrence[]>(`${base(projectKey)}/recurrences`) });
}

export function useTemplateMutations(projectKey: string) {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ["templates", projectKey] });
  const tasksChanged = () => {
    done();
    void qc.invalidateQueries({ queryKey: ["tasks"] });
    void qc.invalidateQueries({ queryKey: ["task"] });
    void qc.invalidateQueries({ queryKey: ["sprints"] });
  };
  return {
    saveTask: useMutation({
      mutationFn: ({ id, ...body }: TaskTemplateRequest & { id?: string }) =>
        id ? put<TaskTemplate>(`${base(projectKey)}/task-templates/${id}`, body) : post<TaskTemplate>(`${base(projectKey)}/task-templates`, body),
      onSuccess: done,
    }),
    removeTask: useMutation({ mutationFn: (id: string) => del(`${base(projectKey)}/task-templates/${id}`), onSuccess: done }),
    fromTemplate: useMutation({ mutationFn: (body: FromTemplateRequest) => post<TaskDetail>(`${base(projectKey)}/tasks/from-template`, body), onSuccess: tasksChanged }),
    saveAs: useMutation({ mutationFn: ({ key, name }: { key: string; name: string }) => post<TaskTemplate>(`${base(projectKey)}/tasks/${key}/save-template`, { name }), onSuccess: done }),
    saveChecklist: useMutation({
      mutationFn: ({ id, ...body }: ChecklistTemplateRequest & { id?: string }) =>
        id ? put<ChecklistTemplate>(`${base(projectKey)}/checklist-templates/${id}`, body) : post<ChecklistTemplate>(`${base(projectKey)}/checklist-templates`, body),
      onSuccess: done,
    }),
    removeChecklist: useMutation({ mutationFn: (id: string) => del(`${base(projectKey)}/checklist-templates/${id}`), onSuccess: done }),
    addChecklist: useMutation({
      mutationFn: ({ key, template_id }: { key: string; template_id: string }) => post<void>(`${base(projectKey)}/tasks/${key}/checklists/from-template`, { template_id }),
      onSuccess: tasksChanged,
    }),
    saveRecurrence: useMutation({
      mutationFn: ({ id, ...body }: RecurrenceRequest & { id?: string }) =>
        id ? put<Recurrence>(`${base(projectKey)}/recurrences/${id}`, body) : post<Recurrence>(`${base(projectKey)}/recurrences`, body),
      onSuccess: done,
    }),
    removeRecurrence: useMutation({ mutationFn: (id: string) => del(`${base(projectKey)}/recurrences/${id}`), onSuccess: done }),
  };
}
